import { range } from 'lodash'
import moment from 'moment'
import ReactDatePicker, { DatePickerProps } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'
import { Calendar } from '../icons'

type Props = Omit<DatePickerProps, 'selected' | 'showMonthYearDropdown'> & {
  label?: string
  labelClassName?: string
  selected: string
  error?: string
  containerClassName?: string
} & (
    | {
        selectsRange: true
        selectsMultiple?: never
        onChange: (
          date: [Date | null, Date | null],
          event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
        ) => void
      }
    | {
        selectsRange?: never
        selectsMultiple: true
        onChange: (
          date: Date[] | null,
          event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
        ) => void
      }
    | {
        selectsRange?: never
        selectsMultiple?: never
        onChange: (date: Date | null, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void
      }
  )

export const BirthdayDateInput = ({
  label,
  labelClassName,
  selected,
  error,
  containerClassName,
  wrapperClassName,
  ...props
}: Props) => {
  const { t } = useTranslation()

  const getYearRange = () => {
    const currentYear = moment().year()
    const startYear = currentYear - 105

    return range(startYear, currentYear + 1)
  }

  const years = getYearRange()

  const months = [
    t('common.January'),
    t('common.February'),
    t('common.March'),
    t('common.April'),
    t('common.May'),
    t('common.June'),
    t('common.July'),
    t('common.August'),
    t('common.September'),
    t('common.October'),
    t('common.November'),
    t('common.December'),
  ]

  return (
    <div className={`flex flex-col gap-y-2 ${containerClassName}`}>
      {label && (
        <span className={`text-white text-sm font-semibold ${labelClassName ? labelClassName : ''}`}>{label}</span>
      )}
      <div className="relative">
        <Calendar className={`absolute top-1/2 -translate-y-1/2 left-4 z-10 pointer-events-none text-navy-900`} />
        <ReactDatePicker
          className="rounded-full border border-navy-900 border-opacity-20 text-navy-900 p-[17px] text-sm pl-12 w-full focus:outline-none"
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => {
            return (
              <div className="flex gap-x-4 justify-center">
                <button className="text-sm font-semibold" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                  {'<'}
                </button>
                <div className="flex gap-x-2">
                  <select
                    className="bg-white rounded px-2 text-sm border border-navy-900 border-opacity-30 focus:outline-none"
                    value={moment(date).year()}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                  >
                    {years.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <select
                    className="bg-white rounded px-2 text-sm border border-navy-900 border-opacity-30 focus:outline-none"
                    value={months[moment(date).month()]}
                    onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                  >
                    {months.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <button className="text-sm font-semibold" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                  {'>'}
                </button>
              </div>
            )
          }}
          selected={selected ? moment(selected).toDate() : moment().subtract(3, 'years').toDate()}
          wrapperClassName={`w-full ${wrapperClassName ? wrapperClassName : ''}`}
          popperClassName="z-20"
          popperPlacement="top-start"
          maxDate={moment().endOf('year').toDate()}
          {...props}
        />
      </div>
      {error && <div className="text-boldOrange -mt-1 font-medium">{error}</div>}
    </div>
  )
}
